import React, { useState, useEffect } from "react";
import api, { extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Calendar as CalendarIcon, BookOpen, Users, Coins, Mail, ListChecks, ContactRound, UserPlus, Download, Eye, Tag, Star, Megaphone, Settings2 } from "lucide-react";
import { formatArabicDateTime, PAGE_SIZE } from "@/pages/admin/utils/format";
import { StatCard } from "@/pages/admin/components/common";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PageShell from "@/components/PageShell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import AdminTasks from "@/pages/admin/AdminTasks";
import AdminClients from "@/pages/admin/AdminClients";
import { BlockedSlotsManager, CapOverridesManager, ServiceBlockedDatesManager } from "@/pages/admin/sections/AvailabilitySection";
import { ManualBookingForm } from "@/pages/admin/sections/ManualBookingSection";
import { BookUploader } from "@/pages/admin/sections/BookSection";
import { CouponsAdmin } from "@/pages/admin/sections/CouponsSection";
import { AdminReviews } from "@/pages/admin/sections/ReviewsSection";
import { AdminWeeklySummaryPanel, AdminDailyDigestPanel } from "@/pages/admin/sections/DigestSection";
import { AdminReviewBackfillPanel } from "@/pages/admin/sections/ReviewBackfillSection";
import { AdminWhatsAppBroadcast } from "@/pages/admin/sections/WhatsAppSection";
import { AdminBroadcast } from "@/pages/admin/sections/EmailToolsSection";
import { AdminServiceVisibility } from "@/pages/admin/sections/ServicesSection";
import { AdminBannerSettings } from "@/pages/admin/sections/BannerSection";
import { BookingsSection } from "@/pages/admin/sections/BookingsSection";
import { PurchasesSection } from "@/pages/admin/sections/PurchasesSection";
import { EmailsSection } from "@/pages/admin/sections/EmailsSection";
import { FinanceSection } from "@/pages/admin/sections/FinanceSection";
import { RescheduleModal } from "@/pages/admin/components/RescheduleModal";

const Admin = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [emails, setEmails] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [tab, setTab] = useState("tasks");

  // Iteration 61 — Tap diagnostics panel. Triggered from a small admin button
  // on the Purchases tab. Surfaces SAFE config info (no secret values) so the
  // admin can verify on production that TAP keys are present + recent failures.
  const [tapDiag, setTapDiag] = useState(null);
  const [tapDiagLoading, setTapDiagLoading] = useState(false);
  const [tapPing, setTapPing] = useState(null);
  const [tapPingLoading, setTapPingLoading] = useState(false);
  const runTapDiagnostics = async () => {
    setTapDiagLoading(true);
    try {
      const { data } = await api.get("/admin/tap/diagnostics");
      setTapDiag(data);
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر تشغيل فحص Tap"));
    } finally {
      setTapDiagLoading(false);
    }
  };
  const runTapPing = async () => {
    setTapPingLoading(true);
    try {
      const { data } = await api.post("/admin/tap/ping");
      setTapPing(data);
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر تنفيذ اختبار اتصال Tap"));
    } finally {
      setTapPingLoading(false);
    }
  };

  // Iteration 48 — client-profile shortcut state. When a row in another tab
  // (bookings / purchases / tasks) requests "open client", we push an object
  // here and switch to the clients tab; <AdminClients> picks it up via prop
  // and either opens the matching profile or shows a not-found toast.
  const [clientLookup, setClientLookup] = useState(null);
  const openClientProfile = (row) => {
    if (!row) return;
    const next = {
      userId: row.user_id || null,
      phone: row.user_phone || null,
      email: row.user_email || null,
      _t: Date.now(),  // unique key so the effect re-runs even if same user
    };
    if (!next.userId && !next.phone && !next.email) {
      toast.error("لم يتم العثور على ملف العميل");
      return;
    }
    setClientLookup(next);
    setTab("clients");
  };

  // Iteration 26 — per-table search state. `*Input` is the live keystroke
  // value; `*Query` is the debounced value that actually triggers a fetch.
  const [bookingsInput, setBookingsInput] = useState("");
  const [bookingsQuery, setBookingsQuery] = useState("");
  // Iteration 62 — Status quick-filter on bookings list. "all" disables the
  // filter; otherwise the literal status string is forwarded to the backend
  // (which already supports `?status=`).
  const [bookingsStatus, setBookingsStatus] = useState("all");
  const [bookingsStatusCounts, setBookingsStatusCounts] = useState({});
  const [purchasesInput, setPurchasesInput] = useState("");
  const [purchasesQuery, setPurchasesQuery] = useState("");
  // Iteration 66 — Status quick-filter on purchases list. "all" disables the
  // filter; otherwise the literal status string is forwarded to the backend.
  const [purchasesStatus, setPurchasesStatus] = useState("all");
  const [purchasesStatusCounts, setPurchasesStatusCounts] = useState({});
  const [emailsInput, setEmailsInput] = useState("");
  const [emailsQuery, setEmailsQuery] = useState("");

  // Iteration 27 — pagination state per table. Server is the source of truth
  // for `total` / `total_pages`; the page index is owned client-side and
  // resets to 1 whenever the search query changes.
  const [bookingsPage, setBookingsPage] = useState(1);
  const [bookingsMeta, setBookingsMeta] = useState({ total: 0, total_pages: 1 });
  const [purchasesPage, setPurchasesPage] = useState(1);
  const [purchasesMeta, setPurchasesMeta] = useState({ total: 0, total_pages: 1 });
  const [emailsPage, setEmailsPage] = useState(1);
  const [emailsMeta, setEmailsMeta] = useState({ total: 0, total_pages: 1 });

  // Iteration 33 — Reschedule modal state.
  // `target` is the booking row being rescheduled. Keeping it as a separate
  // state object so the modal is fully data-driven and no per-row local state.
  const [reschedule, setReschedule] = useState({
    target: null,           // booking object or null
    date: "",               // YYYY-MM-DD (input[type=date] value)
    time: "",               // HH:mm (input[type=time] value)
    note: "",
    saving: false,
  });

  const openReschedule = (b) => {
    let date = "";
    let time = "";
    try {
      const d = new Date(b.slot_iso);
      // Pad helper without zero-pad library
      const pad = (n) => String(n).padStart(2, "0");
      date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch (e) {
      // Best-effort parse; modal will open with empty date/time fields and
      // the admin can pick them manually. No user-facing toast needed.
      if (process.env.NODE_ENV === "development") {
        console.warn("openReschedule: could not parse slot_iso", b?.slot_iso, e);
      }
    }
    setReschedule({ target: b, date, time, note: "", saving: false });
  };

  const closeReschedule = () =>
    setReschedule({ target: null, date: "", time: "", note: "", saving: false });

  const submitReschedule = async () => {
    if (!reschedule.target) return;
    if (!reschedule.date || !reschedule.time) {
      toast.error("اختر التاريخ والوقت أولاً");
      return;
    }
    const slot_iso = `${reschedule.date}T${reschedule.time}:00`;
    setReschedule((r) => ({ ...r, saving: true }));
    try {
      const { data } = await api.post(
        `/admin/bookings/${reschedule.target.id}/reschedule`,
        { slot_iso, note: reschedule.note?.trim() || null },
      );
      toast.success(
        data?.email_live
          ? "تم الترحيل وإرسال إشعار البريد للعميل"
          : "تم الترحيل (الإيميل سُجِّل للمراجعة)",
      );
      closeReschedule();
      refresh();
    } catch (e) {
      toast.error(extractApiErrorMessage(e) || "تعذّر الترحيل");
      setReschedule((r) => ({ ...r, saving: false }));
    }
  };

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/admin/login");
  }, [user, loading, navigate]);

  const refreshSystemStatus = async () => {
    try {
      const r = await api.get("/admin/system-status");
      setSystemStatus(r.data);
    } catch (e) {
      // soft-fail — UI will fall back to "not yet" / unknown messaging
    }
  };

  const refresh = async () => {
    try {
      const bookingsParams = {
        ...(bookingsQuery ? { q: bookingsQuery } : {}),
        // Iteration 83 — preserve the active status filter on ANY refresh
        // (mark-completed / cancel / reschedule) so the highlighted tab and
        // the displayed dataset always match.
        ...(bookingsStatus && bookingsStatus !== "all" ? { status: bookingsStatus } : {}),
        page: bookingsPage,
        limit: PAGE_SIZE,
      };
      const purchasesParams = {
        ...(purchasesQuery ? { q: purchasesQuery } : {}),
        page: purchasesPage,
        limit: PAGE_SIZE,
      };
      const emailsParams = {
        ...(emailsQuery ? { q: emailsQuery } : {}),
        page: emailsPage,
        limit: PAGE_SIZE,
      };
      const [s, b, p, bl, em, sys, inv] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings", { params: bookingsParams }),
        api.get("/admin/purchases", { params: purchasesParams }),
        api.get("/admin/blocked-slots"),
        api.get("/admin/email-log", { params: emailsParams }),
        api.get("/admin/system-status"),
        api.get("/admin/invoices"),
      ]);
      setStats(s.data);
      setBookings(b.data.items);
      setBookingsMeta({ total: b.data.total ?? 0, total_pages: b.data.total_pages ?? 1 });
      // Iteration 83 — if the action emptied the current page (e.g. the last
      // confirmed booking on page 5 became completed), step back to the
      // nearest previous valid page instead of showing an empty table.
      // The page-change effect re-fetches with the same filter/search.
      {
        const bTotalPages = b.data.total_pages ?? 1;
        if ((b.data.items || []).length === 0 && bookingsPage > 1) {
          setBookingsPage(Math.max(1, Math.min(bTotalPages || 1, bookingsPage - 1)));
        }
      }
      setPurchases(p.data.items);
      setPurchasesMeta({ total: p.data.total ?? 0, total_pages: p.data.total_pages ?? 1 });
      setBlocked(bl.data.items);
      setEmails(em.data.items);
      setEmailsMeta({ total: em.data.total ?? 0, total_pages: em.data.total_pages ?? 1 });
      setSystemStatus(sys.data);
      setInvoices(inv.data.items);
    } catch (e) {
      toast.error("تعذّر تحميل البيانات");
    }
  };

  useEffect(() => {
    if (user?.role === "admin") refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Iteration 26 — debounce search inputs (300ms) so each keystroke doesn't
  // hammer the API. The committed query state (`*Query`) drives the fetch
  // via the table-specific effects below.
  useEffect(() => {
    const t = setTimeout(() => setBookingsQuery(bookingsInput.trim()), 300);
    return () => clearTimeout(t);
  }, [bookingsInput]);
  useEffect(() => {
    const t = setTimeout(() => setPurchasesQuery(purchasesInput.trim()), 300);
    return () => clearTimeout(t);
  }, [purchasesInput]);
  useEffect(() => {
    const t = setTimeout(() => setEmailsQuery(emailsInput.trim()), 300);
    return () => clearTimeout(t);
  }, [emailsInput]);

  // Iteration 27 — search query change MUST reset the visible page to 1, so
  // the user never lands on an empty page from a previous filter.
  useEffect(() => { setBookingsPage(1); }, [bookingsQuery]);
  useEffect(() => { setBookingsPage(1); }, [bookingsStatus]);
  useEffect(() => { setPurchasesPage(1); }, [purchasesQuery]);
  useEffect(() => { setPurchasesPage(1); }, [purchasesStatus]);
  useEffect(() => { setEmailsPage(1); }, [emailsQuery]);

  // Re-fetch only the impacted table when its committed query OR page
  // changes — avoids a full-page refresh just to flip the page or filter
  // a single tab.
  useEffect(() => {
    if (user?.role !== "admin") return;
    const params = {
      ...(bookingsQuery ? { q: bookingsQuery } : {}),
      ...(bookingsStatus && bookingsStatus !== "all" ? { status: bookingsStatus } : {}),
      page: bookingsPage,
      limit: PAGE_SIZE,
    };
    api.get("/admin/bookings", { params })
      .then((r) => {
        setBookings(r.data.items);
        setBookingsMeta({ total: r.data.total ?? 0, total_pages: r.data.total_pages ?? 1 });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingsQuery, bookingsStatus, bookingsPage]);

  // Iteration 62 — Count bookings per status (respects current search). Runs
  // alongside the main list so the filter chips show a live count.
  // Iteration 83 — extracted so booking actions can refresh the chips too.
  const loadBookingsCounts = () => {
    if (user?.role !== "admin") return;
    const statuses = ["confirmed", "pending", "canceled", "completed", "failed"];
    const base = bookingsQuery ? { q: bookingsQuery } : {};
    Promise.all([
      api.get("/admin/bookings", { params: { ...base, limit: 1, page: 1 } }),
      ...statuses.map((st) =>
        api.get("/admin/bookings", { params: { ...base, status: st, limit: 1, page: 1 } })
      ),
    ])
      .then(([allR, ...rest]) => {
        const next = { all: allR.data.total ?? 0 };
        statuses.forEach((st, i) => { next[st] = rest[i].data.total ?? 0; });
        setBookingsStatusCounts(next);
      })
      .catch(() => {});
  };
  useEffect(() => {
    loadBookingsCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingsQuery, user]);
  useEffect(() => {
    if (user?.role !== "admin") return;
    const params = {
      ...(purchasesQuery ? { q: purchasesQuery } : {}),
      ...(purchasesStatus && purchasesStatus !== "all" ? { status: purchasesStatus } : {}),
      page: purchasesPage,
      limit: PAGE_SIZE,
    };
    api.get("/admin/purchases", { params })
      .then((r) => {
        setPurchases(r.data.items);
        setPurchasesMeta({ total: r.data.total ?? 0, total_pages: r.data.total_pages ?? 1 });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchasesQuery, purchasesStatus, purchasesPage]);

  // Iteration 66 — Count purchases per status (respects current search).
  useEffect(() => {
    if (user?.role !== "admin") return;
    const statuses = ["paid", "pending", "canceled", "failed"];
    const base = purchasesQuery ? { q: purchasesQuery } : {};
    Promise.all([
      api.get("/admin/purchases", { params: { ...base, limit: 1, page: 1 } }),
      ...statuses.map((st) =>
        api.get("/admin/purchases", { params: { ...base, status: st, limit: 1, page: 1 } })
      ),
    ])
      .then(([allR, ...rest]) => {
        const next = { all: allR.data.total ?? 0 };
        statuses.forEach((st, i) => { next[st] = rest[i].data.total ?? 0; });
        setPurchasesStatusCounts(next);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchasesQuery, user]);
  useEffect(() => {
    if (user?.role !== "admin") return;
    const params = {
      ...(emailsQuery ? { q: emailsQuery } : {}),
      page: emailsPage,
      limit: PAGE_SIZE,
    };
    api.get("/admin/email-log", { params })
      .then((r) => {
        setEmails(r.data.items);
        setEmailsMeta({ total: r.data.total ?? 0, total_pages: r.data.total_pages ?? 1 });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailsQuery, emailsPage]);

  const updateBooking = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("تم التحديث");
      refresh();
      loadBookingsCounts();
    } catch {
      toast.error("تعذّر التحديث");
    }
  };

  // ---- Iteration 36 — Automated weekly backup (status + send-now) ----
  // Hooks must live BEFORE the role-guard early return below.
  const [autoBackup, setAutoBackup] = useState(null);
  const [autoBackupRunning, setAutoBackupRunning] = useState(false);
  const loadAutoBackup = async () => {
    try {
      const { data } = await api.get("/admin/auto-backup/status");
      setAutoBackup(data);
    } catch {
      /* silent — small indicator, not critical */
    }
  };
  useEffect(() => {
    if (user?.role !== "admin") return;
    loadAutoBackup();
  }, [user?.role]);

  if (!user || user.role !== "admin") return null;

  // Index invoices by booking_id and purchase_id so each row's invoice can be
  // surfaced in O(1) without an extra request per row.
  const invoiceByBooking = {};
  const invoiceByPurchase = {};
  for (const inv of invoices) {
    if (inv.booking_id) invoiceByBooking[inv.booking_id] = inv;
    if (inv.purchase_id) invoiceByPurchase[inv.purchase_id] = inv;
  }

  const downloadBackup = async () => {
    const toastId = toast.loading("جارٍ تجهيز النسخة الاحتياطية...");
    try {
      const res = await api.get("/admin/backup", { responseType: "blob", timeout: 60000 });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const filename = `awtadk-backup-${stamp}.zip`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("تم تحميل النسخة الاحتياطية", { id: toastId });
      // refresh the "آخر نسخة احتياطية" indicator without reloading the page
      refreshSystemStatus();
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر تجهيز النسخة الاحتياطية"), { id: toastId });
    }
  };

  // ---- Iteration 36 — Automated weekly backup: run-now handler ----
  const runAutoBackupNow = async () => {
    setAutoBackupRunning(true);
    const toastId = toast.loading("جارٍ تشغيل النسخة الاحتياطية التلقائية...");
    try {
      const { data } = await api.post("/admin/auto-backup/run-now");
      if (data?.attached === false) {
        toast("تم إنشاء النسخة لكن الملف كبير جدًا — حُفظ في قاعدة البيانات", {
          id: toastId,
          icon: "⚠️",
        });
      } else {
        toast.success("تم إرسال النسخة الاحتياطية للإيميل", { id: toastId });
      }
      loadAutoBackup();
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر التشغيل"), { id: toastId });
    } finally {
      setAutoBackupRunning(false);
    }
  };

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-4xl text-[#222222]">لوحة التحكم</h1>
            <p className="text-[#5E5C58] mt-2">إدارة الحجوزات، التوافر، الكتاب الرقمي، وسجلات البريد.</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Button
              type="button"
              onClick={downloadBackup}
              className="bg-[#0A4D3C] hover:bg-[#7a5531] text-white rounded-full px-5 py-2 flex items-center gap-2"
              data-testid="admin-backup-button"
            >
              <Download className="w-4 h-4" />
              تحميل نسخة احتياطية (Backup)
            </Button>
            <p
              className="text-xs text-[#5E5C58] mt-1"
              data-testid="admin-backup-last-timestamp"
            >
              {systemStatus?.last_backup?.at
                ? `آخر نسخة احتياطية: ${formatArabicDateTime(systemStatus.last_backup.at)}`
                : "لم يتم إنشاء نسخة احتياطية بعد"}
            </p>
            <div
              className="mt-2 flex flex-col items-end gap-1 text-xs"
              data-testid="admin-auto-backup-panel"
            >
              <div className="flex items-center gap-2">
                <span className="text-[#5E5C58]">
                  نسخة تلقائية أسبوعية (الأحد) — للإيميل
                </span>
                <Button
                  type="button"
                  onClick={runAutoBackupNow}
                  disabled={autoBackupRunning}
                  variant="outline"
                  className="rounded-full px-3 py-1 h-7 text-xs border-[#0A4D3C] text-[#0A4D3C] hover:bg-[#0A4D3C]/5"
                  data-testid="admin-auto-backup-run-now"
                >
                  {autoBackupRunning ? "..." : "إرسال الآن"}
                </Button>
              </div>
              {autoBackup?.last_run?.at ? (
                <span
                  className="text-[#8B8780]"
                  data-testid="admin-auto-backup-last"
                >
                  آخر إرسال تلقائي: {formatArabicDateTime(autoBackup.last_run.at)}
                  {" · "}
                  {autoBackup.last_run.recipients?.length || 0} مستلم
                </span>
              ) : (
                <span className="text-[#8B8780]">
                  لم يُرسَل أي نسخة تلقائية بعد
                </span>
              )}
            </div>
          </div>
        </div>

        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-8" data-testid="admin-stats">
            <StatCard icon={CalendarIcon} label="إجمالي الحجوزات" value={stats.bookings} />
            <StatCard
              icon={CalendarIcon}
              label="الحجوزات المؤكدة"
              value={stats.bookings_confirmed ?? 0}
            />
            <StatCard icon={BookOpen} label="مبيعات الكتاب" value={stats.purchases_paid} />
            <StatCard
              icon={Coins}
              label="الإيرادات (ر.س)"
              value={Number(stats.revenue_sar).toLocaleString("ar-SA")}
            />
            <StatCard icon={Users} label="عدد العملاء" value={stats.users} />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="mt-10" dir="rtl">
          <TabsList className="bg-[#F6F4EE] rounded-full p-1 flex flex-wrap" data-testid="admin-tabs">
            <TabsTrigger value="tasks" className="rounded-full" data-testid="admin-tab-tasks">
              <ListChecks className="w-3.5 h-3.5 ms-1" /> المهام
            </TabsTrigger>
            <TabsTrigger value="clients" className="rounded-full" data-testid="admin-tab-clients">
              <ContactRound className="w-3.5 h-3.5 ms-1" /> سجل العملاء
            </TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-full" data-testid="admin-tab-bookings">
              الحجوزات
            </TabsTrigger>
            <TabsTrigger value="purchases" className="rounded-full" data-testid="admin-tab-purchases">
              المشتريات
            </TabsTrigger>
            <TabsTrigger value="availability" className="rounded-full" data-testid="admin-tab-availability">
              التوافر
            </TabsTrigger>
            <TabsTrigger value="manual" className="rounded-full" data-testid="admin-tab-manual">
              <UserPlus className="w-3.5 h-3.5 ms-1" /> حجز يدوي
            </TabsTrigger>
            <TabsTrigger value="book" className="rounded-full" data-testid="admin-tab-book">
              الكتاب
            </TabsTrigger>
            <TabsTrigger value="emails" className="rounded-full" data-testid="admin-tab-emails">
              <Mail className="w-3.5 h-3.5 ms-1" /> البريد
            </TabsTrigger>
            <TabsTrigger value="coupons" className="rounded-full" data-testid="admin-tab-coupons">
              <Tag className="w-3.5 h-3.5 ms-1" /> أكواد الخصم
            </TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-full" data-testid="admin-tab-reviews">
              <Star className="w-3.5 h-3.5 ms-1" /> التقييمات
            </TabsTrigger>
            <TabsTrigger value="broadcast" className="rounded-full" data-testid="admin-tab-broadcast">
              <Megaphone className="w-3.5 h-3.5 ms-1" /> إرسال العروض
            </TabsTrigger>
            <TabsTrigger value="banner" className="rounded-full" data-testid="admin-tab-banner">
              <Settings2 className="w-3.5 h-3.5 ms-1" /> إعدادات البانر
            </TabsTrigger>
            <TabsTrigger value="services" className="rounded-full" data-testid="admin-tab-services">
              <Eye className="w-3.5 h-3.5 ms-1" /> الجلسات والخدمات
            </TabsTrigger>
            <TabsTrigger value="finance" className="rounded-full" data-testid="admin-tab-finance">
              <Coins className="w-3.5 h-3.5 ms-1" /> المالية
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tasks" className="mt-6">
            <AdminTasks onOpenClient={openClientProfile} />
          </TabsContent>

          <TabsContent value="clients" className="mt-6">
            <AdminClients
              lookup={clientLookup}
              onLookupHandled={() => setClientLookup(null)}
            />
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            <BookingsSection
              bookings={bookings}
              bookingsInput={bookingsInput}
              setBookingsInput={setBookingsInput}
              bookingsMeta={bookingsMeta}
              bookingsStatus={bookingsStatus}
              setBookingsStatus={setBookingsStatus}
              bookingsStatusCounts={bookingsStatusCounts}
              bookingsPage={bookingsPage}
              setBookingsPage={setBookingsPage}
              invoiceByBooking={invoiceByBooking}
              openClientProfile={openClientProfile}
              openReschedule={openReschedule}
              updateBooking={updateBooking}
            />
          </TabsContent>

          <TabsContent value="purchases" className="mt-6">
            <PurchasesSection
              purchases={purchases}
              purchasesInput={purchasesInput}
              setPurchasesInput={setPurchasesInput}
              purchasesMeta={purchasesMeta}
              purchasesStatus={purchasesStatus}
              setPurchasesStatus={setPurchasesStatus}
              purchasesStatusCounts={purchasesStatusCounts}
              purchasesPage={purchasesPage}
              setPurchasesPage={setPurchasesPage}
              invoiceByPurchase={invoiceByPurchase}
              openClientProfile={openClientProfile}
              tapDiag={tapDiag}
              tapDiagLoading={tapDiagLoading}
              tapPing={tapPing}
              tapPingLoading={tapPingLoading}
              runTapDiagnostics={runTapDiagnostics}
              runTapPing={runTapPing}
            />
          </TabsContent>

          <TabsContent value="availability" className="mt-6">
            <BlockedSlotsManager blocked={blocked} onChange={refresh} />
            <CapOverridesManager />
            <ServiceBlockedDatesManager />
          </TabsContent>

          <TabsContent value="manual" className="mt-6">
            <ManualBookingForm onCreated={refresh} />
          </TabsContent>

          <TabsContent value="book" className="mt-6">
            <BookUploader bookPdf={systemStatus?.book_pdf} onChanged={refreshSystemStatus} />
          </TabsContent>

          <TabsContent value="emails" className="mt-6">
            <EmailsSection
              emails={emails}
              emailsInput={emailsInput}
              setEmailsInput={setEmailsInput}
              emailsMeta={emailsMeta}
              emailsPage={emailsPage}
              setEmailsPage={setEmailsPage}
            />
          </TabsContent>

          <TabsContent value="coupons" className="mt-6">
            <CouponsAdmin />
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            <AdminReviews />
          </TabsContent>

          <TabsContent value="broadcast" className="mt-6">
            <div className="space-y-8">
              <AdminWhatsAppBroadcast />
              <AdminBroadcast />
              <AdminDailyDigestPanel />
              <AdminWeeklySummaryPanel />
              <AdminReviewBackfillPanel />
            </div>
          </TabsContent>

          <TabsContent value="banner" className="mt-6">
            <AdminBannerSettings />
          </TabsContent>

          <TabsContent value="services" className="mt-6">
            <AdminServiceVisibility />
          </TabsContent>

          <TabsContent value="finance" className="mt-6">
            <FinanceSection />
          </TabsContent>
        </Tabs>

        {/* Iteration 33 — Reschedule modal */}
        <RescheduleModal
          reschedule={reschedule}
          setReschedule={setReschedule}
          closeReschedule={closeReschedule}
          submitReschedule={submitReschedule}
        />
      </section>
    </PageShell>
  );
};

export default Admin;
